import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  ChevronRight,
  House,
  Search,
  Shield,
  User,
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { Button, Loading } from '@/components/ui';
import { DonorCard } from '@/components/search/DonorCard';
import { ProfileBottomSheet } from '@/components/profile/ProfileBottomSheet';
import { db } from '@/lib/firebase';
import {
  getNotificationPermissionState,
  resolvePushEnabled,
  type NotificationPermissionState,
} from '@/lib/messaging';
import { useAuthStore } from '@/stores/authStore';
import { useProfileStore } from '@/stores/profileStore';
import { useToastStore } from '@/stores/toastStore';
import type { DonorSummary } from '@/stores/searchStore';
import {
  ANNUAL_INCOME_OPTIONS,
  EDUCATION_LEVELS,
  calculateDonorScore,
  type VerificationRank,
} from '@kulum/shared';

const SETTINGS_DOC_ID = 'preferences';
const RECOMMENDABLE_DONOR_STATUSES = ['email_verified', 'under_review', 'approved'];
const RECOMMENDATION_LIMIT = 3;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface OnboardingTask {
  key: string;
  title: string;
  description: string;
  icon: ReactNode;
  complete: boolean;
  required: boolean;
  ctaLabel?: string;
  onCta?: () => void;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return value;
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }

  return null;
}

function calculateAge(birthDate: Date): number {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

async function fetchReviewStats(userId: string): Promise<{ averageRating: number; totalCount: number }> {
  try {
    const reviewsRef = collection(db, 'reviews');
    const reviewsQuery = query(reviewsRef, where('toUserId', '==', userId));
    const snapshot = await getDocs(reviewsQuery);

    if (snapshot.empty) {
      return { averageRating: 0, totalCount: 0 };
    }

    let total = 0;
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      total += typeof data.rating === 'number' ? data.rating : 0;
    });

    const count = snapshot.docs.length;
    return {
      averageRating: Math.round((total / count) * 10) / 10,
      totalCount: count,
    };
  } catch (error) {
    console.error('Error loading review stats:', error);
    return { averageRating: 0, totalCount: 0 };
  }
}

const getOptionScore = <T extends readonly string[]>(options: T, value?: string): number => {
  if (!value) return 0;
  const index = options.findIndex((option) => option === value);
  return index >= 0 ? index + 1 : 0;
};

const getRankScore = (rank?: VerificationRank | null): number => {
  const scores: Record<string, number> = {
    S: 12,
    A: 10,
    B: 7,
    C: 4,
    D: 2,
  };
  return rank ? scores[rank] || 0 : 0;
};

const calculateHighSpecScore = (donor: DonorSummary): number => {
  let score = 0;

  score += getOptionScore(EDUCATION_LEVELS, donor.educationLevel) * 6;
  score += getOptionScore(ANNUAL_INCOME_OPTIONS, donor.annualIncome) * 5;

  if (typeof donor.height === 'number') {
    if (donor.height >= 180) score += 14;
    else if (donor.height >= 175) score += 10;
    else if (donor.height >= 170) score += 6;
  }

  score += getRankScore(donor.ranks.education) * 2;
  score += getRankScore(donor.ranks.income) * 2;
  score += getRankScore(donor.ranks.spermTest);
  score += getRankScore(donor.ranks.stdTest);

  if (donor.verifications.identity || donor.identityVerified) score += 10;
  if (donor.verifications.education) score += 12;
  if (donor.verifications.income) score += 12;
  if (donor.verifications.spermTest) score += 6;
  if (donor.verifications.stdTest) score += 6;

  if (donor.reviewStats.totalCount > 0) {
    score += donor.reviewStats.averageRating * 3;
    score += Math.min(donor.reviewStats.totalCount, 10);
  }

  if (donor.schoolName) score += 3;
  if (donor.freeText.trim().length >= 80) score += 3;

  return score;
};

const calculateReviewScore = (donor: DonorSummary): number => {
  if (donor.reviewStats.totalCount === 0) return 0;
  return donor.reviewStats.averageRating * 10 + Math.min(donor.reviewStats.totalCount, 20);
};

const calculateRecommendationScore = (donor: DonorSummary, localityScore: number): number =>
  localityScore + calculateHighSpecScore(donor) * 0.45 + calculateReviewScore(donor) * 0.25;

function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone;
}

function OnboardingCard({ task, index }: { task: OnboardingTask; index: number }) {
  return (
    <div
      className="flex w-56 flex-shrink-0 flex-col gap-2 rounded-2xl bg-white p-4 md:w-full"
      style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
    >
      <div className="mb-0.5 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F5EDE2] text-copper">
          {task.complete ? <Check size={14} strokeWidth={2.5} /> : task.icon}
        </div>
        <span className="text-[11px] text-[#AAA]">STEP {index + 1}</span>
      </div>
      <p className="text-[13px] font-semibold leading-snug text-[#1A1A1A]">{task.title}</p>
      <p className="flex-1 text-[11px] leading-relaxed text-[#888]">{task.description}</p>

      {!task.complete && task.ctaLabel && task.onCta ? (
        <button
          type="button"
          onClick={task.onCta}
          className="mt-1 flex items-center gap-0.5 text-left text-[12px] font-medium text-[#B87333]"
        >
          {task.ctaLabel}
          <ChevronRight size={12} />
        </button>
      ) : (
        <span
          className={`text-[12px] font-medium ${task.complete ? 'text-[#2D7D56]' : 'text-[#AAA]'}`}
        >
          {task.complete ? '完了' : task.required ? '対応が必要です' : '任意タスク'}
        </span>
      )}
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);
  const { user, userData } = useAuthStore();
  const { profile, fetchProfile } = useProfileStore();

  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string | null>(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermissionState>(
    getNotificationPermissionState()
  );
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(null);
  const [recommendedDonors, setRecommendedDonors] = useState<DonorSummary[]>([]);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [recommendationError, setRecommendationError] = useState<string | null>(null);
  const [recommendationReloadKey, setRecommendationReloadKey] = useState(0);

  const isDonor = userData?.role === 'donor';
  const isDonee = userData?.role === 'donee';
  const doneePrefecture = profile?.basicInfo?.residence;

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    if (profile?.userId === user.uid) {
      return;
    }

    void fetchProfile(user.uid);
  }, [fetchProfile, profile?.userId, user?.uid]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const updateStandalone = () => {
      setIsStandalone(isStandaloneMode());
    };

    updateStandalone();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateStandalone);
    } else {
      mediaQuery.addListener(updateStandalone);
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      const promptEvent = event as BeforeInstallPromptEvent;
      promptEvent.preventDefault();
      setInstallPromptEvent(promptEvent);
    };
    const handleAppInstalled = () => {
      setIsStandalone(true);
      setInstallPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', updateStandalone);
      } else {
        mediaQuery.removeListener(updateStandalone);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    const syncNotificationPermission = () => {
      setNotificationPermission(getNotificationPermissionState());
    };

    syncNotificationPermission();
    window.addEventListener('focus', syncNotificationPermission);
    document.addEventListener('visibilitychange', syncNotificationPermission);

    return () => {
      window.removeEventListener('focus', syncNotificationPermission);
      document.removeEventListener('visibilitychange', syncNotificationPermission);
    };
  }, []);

  useEffect(() => {
    const loadPushSettings = async () => {
      if (!user?.uid) {
        setPushEnabled(null);
        return;
      }

      try {
        const settingsRef = doc(db, 'users', user.uid, 'settings', SETTINGS_DOC_ID);
        const settingsSnap = await getDoc(settingsRef);
        const rawNotifications = settingsSnap.data()?.notifications;
        setPushEnabled(resolvePushEnabled(rawNotifications));
      } catch (error) {
        console.error('Error loading push settings:', error);
        setPushEnabled(null);
      }
    };

    void loadPushSettings();
  }, [user?.uid]);

  useEffect(() => {
    if (!isDonee) {
      setRecommendedDonors([]);
      return;
    }

    let isActive = true;

    const loadRecommendedDonors = async () => {
      try {
        setIsLoadingRecommendations(true);
        setRecommendationError(null);

        const usersRef = collection(db, 'users');
        const usersQuery = query(
          usersRef,
          where('role', '==', 'donor'),
          where('status', 'in', RECOMMENDABLE_DONOR_STATUSES)
        );
        const usersSnapshot = await getDocs(usersQuery);

        const donorCandidates = await Promise.all(
          usersSnapshot.docs.map(async (userDoc) => {
            const userData = userDoc.data() as {
              displayName?: unknown;
              iconUrl?: unknown;
              identityVerified?: unknown;
              createdAt?: unknown;
            };
            const userId = userDoc.id;

            const profileRef = doc(db, 'profiles', userId);
            const [profileSnap, reviewStats] = await Promise.all([
              getDoc(profileRef),
              fetchReviewStats(userId),
            ]);
            const profileData = profileSnap.exists()
              ? (profileSnap.data() as {
                  basicInfo?: {
                    birthDate?: unknown;
                    residence?: unknown;
                    bloodType?: unknown;
                    annualIncome?: unknown;
                    education?: {
                      level?: unknown;
                      schoolName?: unknown;
                      status?: unknown;
                    };
                  };
                  physicalInfo?: {
                    height?: unknown;
                    weight?: unknown;
                  };
                  donationPolicy?: {
                    methods?: unknown;
                    donationAreas?: unknown;
                  };
                  verificationBadges?: {
                    identity?: unknown;
                    education?: unknown;
                    income?: unknown;
                    spermTest?: unknown;
                    stdTest?: unknown;
                    ranks?: DonorSummary['ranks'];
                  };
                  freeText?: unknown;
                })
              : {};

            const birthDate = toDate(profileData.basicInfo?.birthDate);
            const age = birthDate ? calculateAge(birthDate) : 0;
            const residence =
              typeof profileData.basicInfo?.residence === 'string'
                ? profileData.basicInfo.residence
                : '';
            const ranks = profileData.verificationBadges?.ranks || {};
            const localityScore = doneePrefecture
              ? calculateDonorScore(residence, doneePrefecture, ranks)
              : 0;

            const donationMethods = Array.isArray(profileData.donationPolicy?.methods)
              ? profileData.donationPolicy.methods.filter(
                  (method: unknown): method is string => typeof method === 'string'
                )
              : [];
            const donationAreas = Array.isArray(profileData.donationPolicy?.donationAreas)
              ? profileData.donationPolicy.donationAreas.filter(
                  (area: unknown): area is string => typeof area === 'string'
                )
              : [];
            const createdAt = toDate(userData.createdAt) || new Date();

            const donor = {
              userId,
              displayName:
                typeof userData.displayName === 'string'
                  ? userData.displayName
                  : '不明なドナー',
              iconUrl: typeof userData.iconUrl === 'string' ? userData.iconUrl : undefined,
              age,
              residence,
              height:
                typeof profileData.physicalInfo?.height === 'number'
                  ? profileData.physicalInfo.height
                  : undefined,
              weight:
                typeof profileData.physicalInfo?.weight === 'number'
                  ? profileData.physicalInfo.weight
                  : undefined,
              educationLevel:
                typeof profileData.basicInfo?.education?.level === 'string'
                  ? profileData.basicInfo.education.level
                  : undefined,
              schoolName:
                typeof profileData.basicInfo?.education?.schoolName === 'string'
                  ? profileData.basicInfo.education.schoolName
                  : undefined,
              educationStatus:
                typeof profileData.basicInfo?.education?.status === 'string'
                  ? profileData.basicInfo.education.status
                  : undefined,
              annualIncome:
                typeof profileData.basicInfo?.annualIncome === 'string'
                  ? profileData.basicInfo.annualIncome
                  : undefined,
              bloodType:
                typeof profileData.basicInfo?.bloodType === 'string'
                  ? profileData.basicInfo.bloodType
                  : undefined,
              donationMethods,
              donationAreas,
              freeText:
                typeof profileData.freeText === 'string' ? profileData.freeText : '',
              score: localityScore,
              verifications: {
                identity: profileData.verificationBadges?.identity === true,
                education: profileData.verificationBadges?.education === true,
                income: profileData.verificationBadges?.income === true,
                spermTest: profileData.verificationBadges?.spermTest === true,
                stdTest: profileData.verificationBadges?.stdTest === true,
              },
              ranks,
              identityVerified: userData.identityVerified === true,
              reviewStats,
              createdAt,
            } as DonorSummary;

            return {
              ...donor,
              score: calculateRecommendationScore(donor, localityScore),
            } as DonorSummary;
          })
        );

        const sorted = donorCandidates
          .sort((a, b) => {
            if (a.score === b.score) {
              return (
                a.displayName.localeCompare(b.displayName, 'ja') ||
                a.userId.localeCompare(b.userId)
              );
            }
            return b.score - a.score;
          })
          .slice(0, RECOMMENDATION_LIMIT);

        if (!isActive) {
          return;
        }

        setRecommendedDonors(sorted);
      } catch (error) {
        console.error('Error loading recommended donors:', error);
        if (!isActive) {
          return;
        }
        setRecommendationError('おすすめドナーの取得に失敗しました');
      } finally {
        if (isActive) {
          setIsLoadingRecommendations(false);
        }
      }
    };

    void loadRecommendedDonors();

    return () => {
      isActive = false;
    };
  }, [doneePrefecture, isDonee, recommendationReloadKey]);

  const donorProfileReady = Boolean(
    profile?.basicInfo?.birthDate &&
      profile?.basicInfo?.annualIncome &&
      profile?.basicInfo?.education?.level &&
      profile?.basicInfo?.education?.schoolName
  );
  const doneeProfileReady = Boolean(
    profile?.basicInfo?.birthDate && profile?.basicInfo?.residence
  );
  const isIdentityVerified = userData?.identityVerified === true;
  const notificationSupported = notificationPermission !== 'unsupported';
  const notificationEnabled =
    notificationPermission === 'granted' && pushEnabled === true;

  const handleInstallApp = useCallback(async () => {
    if (isStandalone) {
      return;
    }

    if (installPromptEvent) {
      try {
        await installPromptEvent.prompt();
        await installPromptEvent.userChoice;
        setInstallPromptEvent(null);
        setIsStandalone(isStandaloneMode());
      } catch (error) {
        console.error('Failed to prompt install:', error);
      }
      return;
    }

    const isIos = /iPad|iPhone|iPod/.test(window.navigator.userAgent);
    const message = isIos
      ? 'Safariの共有メニューから「ホーム画面に追加」を選択してください。'
      : 'ブラウザメニューから「ホーム画面に追加」を選択してください。';
    addToast('info', message, 4500);
  }, [addToast, installPromptEvent, isStandalone]);

  const onboardingTasks = useMemo<OnboardingTask[]>(() => {
    if (!userData?.role) {
      return [];
    }

    const profileTask: OnboardingTask = userData.role === 'donor'
      ? {
        key: 'profile',
        title: 'プロフィールを埋めよう',
        description: '生年月日・年収・学歴（最終学歴/学校名）の入力が必要です。',
        icon: <User className="w-5 h-5" />,
        complete: donorProfileReady,
        required: true,
          ctaLabel: 'プロフィールを編集',
          onCta: () => navigate('/mypage/edit'),
        }
      : {
        key: 'profile',
        title: 'プロフィールを埋めよう',
        description: '自己情報が充実しているほど返信率の向上が期待できます。',
        icon: <User className="w-5 h-5" />,
        complete: doneeProfileReady,
        required: false,
          ctaLabel: 'プロフィールを編集',
          onCta: () => navigate('/mypage/edit'),
        };

    const identityTask: OnboardingTask = userData.role === 'donor'
      ? {
        key: 'identity',
        title: '本人確認をしよう',
        description: '本人確認を完了すると、ドニーとのメッセージ導線が有効になります。',
        icon: <Shield className="w-5 h-5" />,
        complete: isIdentityVerified,
        required: true,
          ctaLabel: '本人確認へ進む',
          onCta: () => navigate('/mypage/verification'),
        }
      : {
        key: 'identity',
        title: '本人確認をしよう',
        description: '本人確認をすると画像閲覧申請が使えるようになります。',
        icon: <Shield className="w-5 h-5" />,
        complete: isIdentityVerified,
        required: false,
          ctaLabel: '本人確認へ進む',
          onCta: () => navigate('/mypage/verification'),
        };

    const tasks: OnboardingTask[] = [
      profileTask,
      identityTask,
      {
        key: 'homescreen',
        title: 'ホーム画面に追加しよう',
        description: 'アプリのように素早く開けて、毎日の確認が楽になります。',
        icon: <House className="w-5 h-5" />,
        complete: isStandalone,
        required: false,
        ctaLabel: 'ホーム画面に追加',
        onCta: handleInstallApp,
      },
    ];

    if (isStandalone) {
      tasks.push({
        key: 'notification',
        title: '通知をONにしよう',
        description: notificationSupported
          ? '新着メッセージや申請結果などの重要通知を見逃しにくくなります。'
          : 'この端末ではプッシュ通知に対応していません。',
        icon: <Bell className="w-5 h-5" />,
        complete: notificationSupported ? notificationEnabled : false,
        required: false,
        ctaLabel: notificationSupported ? '通知を設定する' : undefined,
        onCta: notificationSupported ? () => navigate('/mypage/notifications') : undefined,
      });
    }

    return tasks;
  }, [
    donorProfileReady,
    doneeProfileReady,
    handleInstallApp,
    isIdentityVerified,
    isStandalone,
    navigate,
    notificationEnabled,
    notificationSupported,
    userData?.role,
  ]);

  const requiredTaskCount = onboardingTasks.filter((task) => task.required).length;
  const completedRequiredTaskCount = onboardingTasks.filter(
    (task) => task.required && task.complete
  ).length;

  return (
    <>
      <div className="min-h-full bg-[#F7F4EF]">
        <div className="md:mx-auto md:max-w-6xl md:px-6 md:py-6">
          <div className="px-5 pb-5 pt-14 md:px-0 md:pt-0">
            <p className="mb-1 text-[11px] font-semibold tracking-[0.2em] text-[#B87333]">KULUM</p>
            <h1 className="text-[22px] font-semibold leading-snug text-[#1A1A1A]">
              ようこそ、{userData?.displayName || 'ゲスト'}さん
            </h1>
            <p className="mt-1 text-[13px] text-[#999]">
              {isDonor
                ? 'ドナー向けの準備タスクを確認できます。'
                : '必要なものから少しずつ進めましょう。'}
            </p>
          </div>

          <div className="space-y-7 pb-10 md:space-y-8 md:pb-8">
            <section className="px-4 md:px-0">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold text-[#1A1A1A]">はじめかた</p>
                  {isDonor ? (
                    <p className="mt-1 text-[11px] text-[#AAA]">
                      重要タスク {completedRequiredTaskCount}/{requiredTaskCount}
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-[#AAA]">より使いやすくするヒント</p>
                  )}
                </div>
                <Link
                  to="/mypage"
                  className="flex items-center gap-0.5 text-[12px] text-[#B87333]"
                >
                  マイページ
                  <ChevronRight size={13} />
                </Link>
              </div>

              <div
                className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-2 md:gap-3 md:overflow-visible md:px-0 lg:grid-cols-3"
                style={{ scrollbarWidth: 'none' }}
              >
                {onboardingTasks.map((task, index) => (
                  <OnboardingCard key={task.key} task={task} index={index} />
                ))}
              </div>
            </section>

            {isDonee && (
              <section className="px-4 md:px-0">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-semibold text-[#1A1A1A]">おすすめドナー</p>
                    {doneePrefecture && (
                      <p className="mt-1 text-[11px] text-[#AAA]">
                        あなたの地域（{doneePrefecture}）を優先表示
                      </p>
                    )}
                  </div>
                  <Link
                    to="/search"
                    className="flex items-center gap-0.5 text-[12px] text-[#B87333]"
                  >
                    すべて見る
                    <ChevronRight size={13} />
                  </Link>
                </div>

                {isLoadingRecommendations && (
                  <div
                    className="flex justify-center rounded-2xl bg-white py-10"
                    style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
                  >
                    <Loading />
                  </div>
                )}

                {!isLoadingRecommendations && recommendationError && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-[13px] text-red-700">
                    <p>{recommendationError}</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-3 !px-3 !py-1.5"
                      onClick={() => setRecommendationReloadKey((prev) => prev + 1)}
                    >
                      再取得
                    </Button>
                  </div>
                )}

                {!isLoadingRecommendations &&
                  !recommendationError &&
                  recommendedDonors.length > 0 && (
                    <>
                      <div
                        className="overflow-hidden rounded-2xl bg-white md:hidden"
                        style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
                      >
                        {recommendedDonors.map((donor, index) => (
                          <div key={donor.userId}>
                            <DonorCard donor={donor} onOpenProfile={setSelectedProfileUserId} />
                            {index < recommendedDonors.length - 1 && (
                              <div className="ml-[80px] h-px bg-[#F0EDE8]" />
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-3">
                        {recommendedDonors.map((donor) => (
                          <div
                            key={donor.userId}
                            className="overflow-hidden rounded-2xl bg-white"
                            style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
                          >
                            <DonorCard donor={donor} onOpenProfile={setSelectedProfileUserId} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                {!isLoadingRecommendations &&
                  !recommendationError &&
                  recommendedDonors.length === 0 && (
                    <div
                      className="rounded-2xl bg-white px-4 py-10 text-center"
                      style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
                    >
                      <Search className="mx-auto mb-3 h-10 w-10 text-neutral-300" />
                      <p className="text-sm text-neutral-600">
                        おすすめ候補の取得に必要なデータがまだ不足しています。
                      </p>
                      <div className="mt-4 flex items-center justify-center gap-2">
                        <Button size="sm" onClick={() => navigate('/mypage/edit')}>
                          プロフィールを編集
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => navigate('/search')}>
                          検索画面へ
                        </Button>
                      </div>
                    </div>
                  )}
              </section>
            )}
          </div>
        </div>
      </div>

      <ProfileBottomSheet
        userId={selectedProfileUserId}
        isOpen={selectedProfileUserId !== null}
        onClose={() => setSelectedProfileUserId(null)}
      />
    </>
  );
}
